import {
  DataGrid,
  useGridApiRef,
  gridPaginatedVisibleSortedGridRowIdsSelector,
} from "@mui/x-data-grid";
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Package,
  Edit,
  Eye,
  X,
  GripHorizontal,
  FileText,
  CheckCircle,
  XCircle,
  CalendarClock,
} from "lucide-react";
import Modal from "../common/CommonModal";
import dayjs from "dayjs";
import CloseDocumentModal from "./CloseDocumentModal";
import DocumentManagerModal from "./DocumentManagerModal";

const InvoiceDataGrid = ({
  setColumnVisibilityModel,
  columnVisibilityModel,
  rows,
  columns,
  loading,
  selectionModel,
  setSelectionModel,
  setSelectedRowData,
  selectedRowData,
  onEdit,
  onView,
  pageNumber,
  setPageNumber,
  currentEnd,
  totalCount,
  currentStart,
  showSidebar,
  docLabel,
  enableF5Action = false,
  config,
  refreshDocumentList,
  f5Mode,
  modalType = "close",
  setModalType,
  showApproveReject = false,
  reviewMode = "SALESORDER",
  isSalesOrderEditable,
  onSchedule,
  showSchedule = false,
  showEdit = true,
  showUpdateButton = false,
  showView = true,
}) => {
  const apiRef = useGridApiRef();
  const [showActionBar, setShowActionBar] = useState(false);
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const actionBarRef = useRef(null);
  const containerRef = useRef(null);
  const [isF5ModalOpen, setIsF5ModalOpen] = useState(false);
  const [f5InputValue, setF5InputValue] = useState("");
  const [f5Loading, setF5Loading] = useState(false);

  const [isApproveRejectModalOpen, setIsApproveRejectModalOpen] =
    useState(false);
  const [approvalStatus, setApprovalStatus] = useState(""); // "approve" or "reject"
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Update Status Modal State
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [updateStatusValue, setUpdateStatusValue] = useState("");
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);

  const calculateInitialPosition = useCallback(() => {
    if (containerRef.current && actionBarRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const actionBarRect = actionBarRef.current.getBoundingClientRect();

      return {
        x: Math.max(0, containerRect.width - actionBarRect.width - 16),
        y: 100,
      };
    }
    return { x: 16, y: 100 };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (showActionBar && position) {
        const newPosition = calculateInitialPosition();
        setPosition(newPosition);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showActionBar, position, calculateInitialPosition]);

  useEffect(() => {
    if (rows?.length > 0 && apiRef.current) {
      setTimeout(() => {
        const visibleRows =
          gridPaginatedVisibleSortedGridRowIdsSelector(apiRef);
        if (visibleRows.length > 0) {
          const firstRowId = visibleRows[0];
          const firstRowData = apiRef.current.getRow(firstRowId);
          if (firstRowData) {
            setSelectionModel([firstRowId]);
            setSelectedRowData(firstRowData);
            setShowActionBar(true);
          }
        }
      }, 100);
    }
  }, [rows]);

  useEffect(() => {
    if (rows?.length > 0) {
      setPosition(null);
    }
  }, [rows]);

  useEffect(() => {
    if (showActionBar && !position) {
      setTimeout(() => {
        setPosition(calculateInitialPosition());
      }, 0);
    }
  }, [showActionBar, calculateInitialPosition]);

  const handleRowClick = (params) => {
    setSelectionModel([params.id]);
    setSelectedRowData(params.row);
    setShowActionBar(true);
  };

  const handleMouseDown = (e) => {
    if (!position) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current || !actionBarRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const actionBarRect = actionBarRef.current.getBoundingClientRect();

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Constrain to container bounds
    const maxX = containerRect.width - actionBarRect.width;
    const maxY = containerRect.height - actionBarRect.height;

    const constrainedX = Math.max(0, Math.min(newX, maxX));
    const constrainedY = Math.max(0, Math.min(newY, maxY));

    setPosition({ x: constrainedX, y: constrainedY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };
  }, [isDragging, dragStart, position]);

  useEffect(() => {
    if (showActionBar && showSidebar !== undefined) {
      const timeout = setTimeout(() => {
        setPosition(calculateInitialPosition());
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [showSidebar, showActionBar, calculateInitialPosition]);

  const handleF5Action = useCallback((rowData) => {
    const status = (
      rowData["STATUS"] ||
      rowData["Status"] ||
      rowData["status"] ||
      ""
    ).toUpperCase();
    const billStatus = (rowData["Bill Status"] || "").toUpperCase();

    if (
      ["Y", "INVOICED", "CLOSED"].includes(status) ||
      billStatus === "INVOICED"
    ) {
      alert("This document is already CLOSED or INVOICED!");
      return;
    }

    setIsF5ModalOpen(true);
    setF5InputValue("");
  }, []);

  useEffect(() => {
    if (!enableF5Action) return;

    const handleKeyDown = (e) => {
      if (e.key === "F5" && selectedRowData) {
        e.preventDefault();
        handleF5Action(selectedRowData);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableF5Action, selectedRowData, handleF5Action]);

  //Close DOCUMENT
  const handleF5Submit = async () => {
    if (!f5InputValue.trim()) {
      alert("Please enter a value");
      return;
    }

    setF5Loading(true);
    try {
      const payload = [
        {
          CmpCode: config.COMPANY_CODE,
          mode: f5Mode,
          DocNo:
            selectedRowData["DO NO"] ||
            selectedRowData["SO NO"] ||
            selectedRowData["PO NO"],
          deptno: config.DEPARTMENT_NO,
          Status: "CLOSED",
          Comments: f5InputValue,
          UserId: config?.CURRENT_USER,
          created_on: dayjs().format("YYYY-MM-DD"),
        },
      ];

      const response = await fetch(`${config.BASE_URL}/api/ProposalStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to process request");
      }

      const result = await response.json();
      console.log("F5 Action Result:", result);
      alert(`${docLabel} Closed successfully!`);
      setIsF5ModalOpen(false);
      setF5InputValue("");

      await refreshDocumentList();
    } catch (error) {
      console.error("Error processing F5 action:", error);
      alert("Failed to process action: " + error.message);
    } finally {
      setF5Loading(false);
    }
  };
  // Handle Update Status button click
  const handleUpdateStatusClick = () => {
    setUpdateStatusValue("");
    setIsUpdateStatusModalOpen(true);
  };

  // Handle Update Status submit
  const handleUpdateStatusSubmit = async () => {
    if (!updateStatusValue.trim()) {
      alert("Please enter a status to update.");
      return;
    }
    setUpdateStatusLoading(true);
    try {
      const soUid =
        selectedRowData?.SoUid ||
        selectedRowData?.soUid ||
        selectedRowData?.woNo;
      if (!soUid) {
        alert("Work Order unique ID not found in selected row.");
        setUpdateStatusLoading(false);
        return;
      }
      const payload = [
        {
          CmpCode: config.COMPANY_CODE,
          mode: "WOPRODSTAT",
          DocNo: soUid,
          DeptNO: config.DEPARTMENT_NO,
          Status: "WORKORDER",
          Comments: updateStatusValue,
          UserID: config?.CURRENT_USER,
          created_on: new Date().toISOString().slice(0, 10),
        },
      ];
      const response = await fetch(`${config.BASE_URL}/api/ProposalStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update status");
      alert("Status updated successfully!");
      setIsUpdateStatusModalOpen(false);
      setUpdateStatusValue("");
      await refreshDocumentList();
    } catch (err) {
      alert("Failed to update status: " + err.message);
    } finally {
      setUpdateStatusLoading(false);
    }
  };

  // Handle approve/reject button click
  const handleApproveRejectClick = async (status) => {
    const docNo = selectedRowData["SO NO"] || selectedRowData["PO NO"];

    // Only check for Sales Orders
    if (reviewMode === "SALESORDER" && docNo) {
      const editable = await isSalesOrderEditable(docNo);
      if (!editable) {
        alert(
          `Sales Order ${docNo} cannot be approved/rejected because a delivery order has already been created.`,
        );
        return; // Exit early, don't open modal
      }
    }

    // Proceed to open modal if editable
    setApprovalStatus(status);
    setRejectionReason("");
    setIsApproveRejectModalOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (reviewMode !== "INVOICE" && approvalStatus === "reject" && !rejectionReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    setApprovalLoading(true);
    try {
      const isInvoice = reviewMode === "INVOICE";
      const docNo = isInvoice
        ? selectedRowData?.INVNO || selectedRowData?.invNo
        : selectedRowData["SO NO"] || selectedRowData["PO NO"];

      const status = isInvoice
        ? approvalStatus
        : approvalStatus === "approve" ? "Approved" : "Rejected";

      const comments = isInvoice
        ? approvalStatus
        : approvalStatus === "reject" ? rejectionReason : "Approved";

      const payload = [
        {
          CmpCode: config.COMPANY_CODE,
          mode: reviewMode,
          DocNo: docNo,
          DeptNO: config.DEPARTMENT_NO,
          Status: status,
          Comments: comments,
          UserID: config?.CURRENT_USER,
          created_on: dayjs().format("YYYY-MM-DD"),
        },
      ];

      const response = await fetch(`${config.BASE_URL}/api/ProposalStatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to process approval");
      }

      const result = await response.json();
      console.log("Approval Result:", result);
      alert(
        isInvoice
          ? `Invoice status updated to "${status}" successfully!`
          : `${reviewMode === "PURCHASEORDER" ? "Purchase" : "Sales"} Order ${approvalStatus === "approve" ? "Approved" : "Rejected"} successfully!`,
      );
      setIsApproveRejectModalOpen(false);
      setRejectionReason("");

      await refreshDocumentList();
    } catch (error) {
      console.error("Error processing approval:", error);
      alert("Failed to process approval: " + error.message);
    } finally {
      setApprovalLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative border border-gray-200 rounded-lg overflow-hidden"
    // style={{ height: 700, width: "100%" }}
    >
      {/* Draggable Floating Action Bar */}
      {showActionBar && selectedRowData && (
        <div
          ref={actionBarRef}
          className="absolute z-10 transition-all duration-200 ease-out"
          style={{
            left: position?.x || 0,
            top: position?.y || 24,
            visibility: position ? "visible" : "hidden",
            maxWidth: "calc(100% - 40px)",
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-lg shadow-black/10 p-3 flex items-center gap-3 w-full max-w-md">
            {/* Drag Handle */}
            <div
              className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${isDragging
                ? "bg-blue-100 text-blue-600 cursor-grabbing"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-grab"
                }`}
              onMouseDown={handleMouseDown}
            >
              <GripHorizontal size={16} />
            </div>

            <div className="flex-1 flex items-center gap-2 min-w-0">
              <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
                <FileText size={14} className="text-blue-600" />
              </div>
              <div className="text-sm min-w-0 flex-1">
                <div className="font-semibold text-gray-800 truncate">
                  {selectedRowData?.INVNO ||
                    selectedRowData["DO NO"] ||
                    selectedRowData["SO NO"] ||
                    selectedRowData["QUOT NO"] ||
                    selectedRowData["SR_NO"] ||
                    selectedRowData["PO NO"] ||
                    selectedRowData["SRVNO"] ||
                    selectedRowData["DEMNO"] ||
                    selectedRowData["Wo_No"] ||
                    selectedRowData["WO NO"] ||
                    selectedRowData["PROD_NO"] ||
                    selectedRowData.TROUT_NO ||
                    selectedRowData.TRIN_NO ||
                    selectedRowData.CLAIM_NO ||
                    selectedRowData["GIT NO"] ||
                    selectedRowData["TRFRNO"] ||
                    selectedRowData["PR NO"] ||
                    "N/A"}
                </div>
                <div
                  className="text-xs text-gray-500 truncate"
                  title={
                    selectedRowData?.CUSTOMER ||
                    selectedRowData?.CUST_ACC ||
                    selectedRowData?.SUPPLIER ||
                    selectedRowData?.SALESPERSON ||
                    selectedRowData?.Customer ||
                    selectedRowData?.Customer ||
                    selectedRowData?.TROUT_NO ||
                    selectedRowData.TRIN_NO ||
                    selectedRowData.CLAIM_NO ||
                    selectedRowData.TROUT_NO ||
                    selectedRowData["GIT NO"] ||
                    selectedRowData["TRFRNO"] ||
                    `${docLabel} Selected`
                  }
                >
                  {selectedRowData?.CUSTOMER
                    ? selectedRowData?.INVNO !== selectedRowData?.CUSTOMER
                      ? selectedRowData.CUSTOMER
                      : `${docLabel} Selected`
                    : selectedRowData?.CUST_ACC ||
                    selectedRowData?.SUPPLIER ||
                    selectedRowData?.SALESPERSON ||
                    selectedRowData?.Customer ||
                    selectedRowData?.TROUT_NO ||
                    selectedRowData?.TRIN_NO ||
                    selectedRowData.CLAIM_NO ||
                    selectedRowData.TROUT_NO ||
                    selectedRowData["GIT NO"] ||
                    selectedRowData["TRFRNO"] ||
                    `${docLabel} Selected`}
                </div>
              </div>
            </div>

            <div className="flex gap-1.5 flex-shrink-0">
              {/* F5 Action Button - Only show on mobile/tablet */}
              {enableF5Action && (
                <button
                  onClick={() => handleF5Action?.(selectedRowData)}
                  className="lg:hidden px-2 sm:px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="Quick Action (F5)"
                >
                  <span className="text-xs font-bold">F5</span>
                </button>
              )}

              {showApproveReject && (
                <button
                  onClick={() => handleApproveRejectClick("approve")}
                  className="px-2 sm:px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="Approve/Reject"
                >
                  <CheckCircle size={14} />
                  <span className="hidden md:inline">Review</span>
                </button>
              )}
              {showUpdateButton && (
                <button
                  onClick={handleUpdateStatusClick}
                  className="px-2 sm:px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="Update Status"
                >
                  <CheckCircle size={14} />
                  <span className="hidden md:inline">Update </span>
                </button>
              )}
              {/* Update Status Modal */}

              {showView && (
                <button
                  onClick={() => onView?.(selectedRowData)}
                  className="px-2 sm:px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="View Invoice"
                >
                  <Eye size={14} />
                  <span className="hidden md:inline">View</span>
                </button>)}

              {showEdit && (
                <button
                  onClick={() => onEdit?.(selectedRowData)}
                  className="px-2 sm:px-3 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="Edit Invoice"
                >
                  <Edit size={14} />
                  <span className="hidden md:inline">Edit</span>
                </button>
              )}
              {showSchedule && (
                <button
                  onClick={() => onSchedule?.(selectedRowData)}
                  className="px-2 sm:px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                  title="Schedule Task"
                >
                  <CalendarClock size={14} />
                  <span className="hidden md:inline">Schedule</span>
                </button>
              )}

              <button
                onClick={() => {
                  setShowActionBar(false);
                  setPosition(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95"
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative border border-gray-200 rounded-lg overflow-hidden"
        style={{
          height: "calc(100vh - 200px)",
          width: "100%",
        }}
      >
        <div className="flex flex-col border rounded-lg overflow-hidden h-full">
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGrid
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              loading={loading}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(newSelection) => {
                setSelectionModel(newSelection);
                if (apiRef.current && newSelection.length > 0) {
                  const row = apiRef.current.getRow(newSelection[0]);
                  setSelectedRowData(row);
                  setShowActionBar(true);
                } else {
                  setShowActionBar(false);
                  setPosition(null);
                }
              }}
              onRowClick={handleRowClick}
              pagination={false}
              checkboxSelection={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              rowHeight={35}
              sx={{
                border: "none",
                borderRadius: "10px",
                fontFamily: '"Figtree", sans-serif',

                // --- Header styling ---
                "& .MuiDataGrid-columnHeaders": {
                  background: "#e2e8f0",
                  color: "black",
                  fontSize: "0.9rem",
                  borderBottom: "2px solid rgba(255, 255, 255, 0.2)",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  letterSpacing: "0.03em",
                },

                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                  padding: "1px 8px",
                  fontSize: "0.85rem",
                },

                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                },

                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "rgba(59, 130, 246, 0.08)",
                  borderLeft: "3px solid rgb(59, 130, 246)",
                },

                "& .MuiDataGrid-footerContainer": {
                  display: "none",
                },
              }}
              slotProps={{
                loadingOverlay: { variant: "linear-progress" },
              }}
              slots={{
                noRowsOverlay: () => (
                  <div className="flex flex-col items-center justify-center h-full space-y-4">
                    <Package className="h-16 w-16 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-600">
                      No products found
                    </h3>
                    <p className="text-gray-400">
                      Try adjusting your search criteria
                    </p>
                  </div>
                ),
              }}
            />
          </div>

          {/* Custom Pagination */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-200 ">
            {rows?.length > 0 ? (
              <span className="text-sm text-gray-600">
                Showing {currentStart}–{currentEnd} of {totalCount}
              </span>
            ) : (
              <span className="text-sm text-gray-500 italic">
                No results found
              </span>
            )}
            <div className="space-x-2">
              <button
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber === 1}
                className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPageNumber((p) => p + 1)}
                disabled={currentEnd >= totalCount}
                className="px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isUpdateStatusModalOpen}
        onClose={() => setIsUpdateStatusModalOpen(false)}
        title={"Update Production Status"}
      >
        <div className="space-y-4 p-2 px-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Production Number:</p>
            <p className="font-semibold text-gray-900">
              {selectedRowData?.PROD_NO ||
                selectedRowData?.prodNo ||
                selectedRowData?.Wo_No ||
                selectedRowData?.woNo ||
                "N/A"}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <textarea
              value={updateStatusValue}
              onChange={(e) => setUpdateStatusValue(e.target.value)}
              placeholder="Enter new status..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              disabled={updateStatusLoading}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={() => setIsUpdateStatusModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={updateStatusLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateStatusSubmit}
              disabled={updateStatusLoading || !updateStatusValue.trim()}
              className="px-4 py-2 rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateStatusLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </Modal>

      {/* F5 Modal */}
      <Modal
        isOpen={isF5ModalOpen}
        onClose={() => setIsF5ModalOpen(false)}
        title={
          modalType === "manager"
            ? `Document Manager - ${selectedRowData?.SRVNO || selectedRowData?.["PO No"]
            }`
            : `Close ${docLabel}`
        }
      >
        {modalType === "close" && (
          <CloseDocumentModal
            docLabel={docLabel}
            selectedRowData={selectedRowData}
            f5InputValue={f5InputValue}
            setF5InputValue={setF5InputValue}
            handleF5Submit={handleF5Submit}
            f5Loading={f5Loading}
            onClose={() => setModalType(null)}
          />
        )}

        {modalType === "manager" && (
          <DocumentManagerModal
            selectedRowData={selectedRowData}
            config={config}
          />
        )}
      </Modal>

      {/* Approve/Reject Modal */}
      <Modal
        isOpen={isApproveRejectModalOpen}
        onClose={() => setIsApproveRejectModalOpen(false)}
        title={reviewMode === "INVOICE"
          ? "Update Invoice Status"
          : `${approvalStatus === "approve" ? "Approve" : "Reject"} ${reviewMode === "PURCHASEORDER" ? "Purchase" : "Sales"} Order`
        }
      >
        <div className="space-y-4 p-2 px-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">
              {reviewMode === "INVOICE"
                ? "Invoice Number:"
                : `${reviewMode === "PURCHASEORDER" ? "Purchase" : "Sales"} Order Number:`}
            </p>
            <p className="font-semibold text-gray-900">
              {reviewMode === "INVOICE"
                ? selectedRowData?.INVNO || selectedRowData?.invNo || "N/A"
                : selectedRowData?.["SO NO"] || selectedRowData["PO NO"] || "N/A"}
            </p>
          </div>

          <div className="flex gap-2">
            {reviewMode === "INVOICE" ? (
              <>
                <button
                  onClick={() => setApprovalStatus("Pending")}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${approvalStatus === "Pending"
                    ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-yellow-300"
                    }`}
                >
                  <CalendarClock className="inline-block mr-2" size={18} />
                  Pending
                </button>
                <button
                  onClick={() => setApprovalStatus("Submitted")}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${approvalStatus === "Submitted"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-green-300"
                    }`}
                >
                  <CheckCircle className="inline-block mr-2" size={18} />
                  Submitted
                </button>

              </>
            ) : (
              <>
                <button
                  onClick={() => setApprovalStatus("approve")}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${approvalStatus === "approve"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-green-300"
                    }`}
                >
                  <CheckCircle className="inline-block mr-2" size={18} />
                  Approve
                </button>
                <button
                  onClick={() => setApprovalStatus("reject")}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${approvalStatus === "reject"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-red-300"
                    }`}
                >
                  <XCircle className="inline-block mr-2" size={18} />
                  Reject
                </button>
              </>
            )}
          </div>

          {reviewMode !== "INVOICE" && approvalStatus === "reject" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this sales order..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
              />
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={() => setIsApproveRejectModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={approvalLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleApprovalSubmit}
              disabled={approvalLoading || !approvalStatus}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${reviewMode === "INVOICE"
                ? "bg-blue-600 hover:bg-blue-700"
                : approvalStatus === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {approvalLoading
                ? "Processing..."
                : reviewMode === "INVOICE"
                  ? "Update Status"
                  : `${approvalStatus === "approve" ? "Approve" : "Reject"} Order`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceDataGrid;
